// Deterministic clinical-decision-support rules.
// Input:   { profile, labs, medicines, allergyFlags, comorbidities, diagnosisPrimary }
// Output:  { alerts:      [{level, msg, guideline?, rule_id, medicine?}],
//            requiredLabs:[{lab, reason, rule_id}],
//            interactions:[{pair, level, msg, rule_id}] }
//
// Levels:  block  (contraindicated; hard stop)
//          major  (serious risk; needs override)
//          caution
//          info   (guideline suggestion)
//
// The engine is intentionally client-side, JSON-serialisable, and cheap.
// AI suggestions (Gemini 3 Pro) are additive; this layer is always on.

const clean = (s) => (s || "").toLowerCase();
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(",", ""));
  return isFinite(n) ? n : null;
};
const medText = (m) => clean(`${m.brand_name} ${m.generic_name} ${m.composition}`);

// ---------- drug-class matchers ----------
const MATCHERS = {
  metformin: /\bmetformin\b/,
  sglt2: /\b(empa|dapa|cana|ertu)gliflozin\b|\bsotagliflozin\b/,
  glp1: /\b(lira|sema|dula|exena|liraglut|semaglut|dulaglut|tirzepatide)/,
  sulfonylurea: /\b(gli(m|c|p|b)|glimepiride|gliclazide|glipizide|glibenclamide|glyburide|glimeperide)\b/,
  dpp4: /\b(sita|vilda|saxa|lina|teneli|alogli|gemi)gliptin\b/,
  pioglitazone: /\bpioglitazone\b|\brosiglitazone\b/,
  insulin: /\binsulin\b|\b(glargine|detemir|degludec|aspart|lispro|glulisine|regular|nph|mixtard)\b/,
  statin: /\b(atorva|rosuva|simva|prava|fluva|pitava|lova)statin\b/,
  fibrate: /\bfenofibrate\b|\bgemfibrozil\b|\bbezafibrate\b/,
  ezetimibe: /\bezetimibe\b/,
  acei: /\b(rami|enala|lisin|perindo|capto|fosin|zofen|imida|quina|benaz|trando|moexi)pril\b/,
  arb: /\b(losartan|valsartan|telmisartan|olmesartan|irbesartan|candesartan|azilsartan|eprosartan)\b/,
  arni: /\bsacubitril\b/,
  bb: /\b(metoprolol|bisoprolol|carvedilol|nebivolol|atenolol|propranolol|labetalol|esmolol)\b/,
  ccb: /\b(amlodipine|nifedipine|felodipine|verapamil|diltiazem|nitrendipine)\b/,
  ccb_ndhp: /\b(verapamil|diltiazem)\b/,
  diuretic_thiazide: /\b(hydrochlorothiazide|hctz|indapamide|chlorthalidone|chlortalidone|xipamide|metolazone)\b/,
  diuretic_loop: /\b(furosemide|torasemide|torsemide|bumetanide|ethacrynic)\b/,
  spironolactone: /\bspironolactone\b|\beplerenone\b/,
  ppi: /\b(omeprazole|esomeprazole|pantoprazole|lansoprazole|rabeprazole|dexlansoprazole)\b/,
  clopidogrel: /\bclopidogrel\b/,
  aspirin: /\b(aspirin|acetylsalicylic)\b/,
  warfarin: /\bwarfarin\b|\bacenocoumarol\b/,
  doac: /\b(apixaban|rivaroxaban|dabigatran|edoxaban)\b/,
  nsaid: /\b(ibuprofen|diclofenac|naproxen|etoricoxib|celecoxib|ketorolac|piroxicam|indomethacin|aceclofenac|mefenamic|nimesulide|meloxicam|nsaid)\b/,
  paracetamol: /\b(paracetamol|acetaminophen)\b/,
  nitrate: /\b(isosorbide|nitroglycerin|glyceryl trinitrate|gtn)\b/,
  pde5: /\b(sildenafil|tadalafil|vardenafil|avanafil)\b/,
  macrolide: /\b(clarithromycin|erythromycin|azithromycin|roxithromycin)\b/,
  fq: /\b(cipro|levo|moxi|oflox|gemi)floxacin\b/,
  amiodarone: /\bamiodarone\b/,
  digoxin: /\bdigoxin\b/,
  levothyroxine: /\b(levothyroxine|thyroxine|eltroxin)\b/,
  calcium_iron: /\b(calcium|ferrous|iron|sucralfate|aluminum|magnesium hydroxide)\b/,
  benzo: /\b(alprazolam|clonazepam|diazepam|lorazepam|midazolam|nitrazepam|temazepam)\b/,
  contrast: /\biodinated contrast\b|\bcontrast\b|\bcontrast media\b/,
  qtProlonging: /\b(ondansetron|hydroxychloroquine|methadone|haloperidol|ziprasidone|escitalopram|citalopram|domperidone|erythromycin|clarithromycin|moxifloxacin|levofloxacin)\b/,
  colchicine: /\bcolchicine\b/,
};
export const drugClass = (m) => {
  const t = medText(m);
  const cls = [];
  for (const [k, rx] of Object.entries(MATCHERS)) if (rx.test(t)) cls.push(k);
  return cls;
};
const has = (list, cls) => list.some((m) => drugClass(m).includes(cls));
const findMed = (list, cls) => list.find((m) => drugClass(m).includes(cls));

// ---------- guideline citations ----------
const G = {
  ADA_METFORMIN: "ADA 2026 §11.3 (Metformin CI when eGFR<30, review 30-45)",
  ADA_SGLT2: "ADA 2026 §11.5 / KDIGO 2024 (SGLT2i CV/renal benefit)",
  ADA_LDL: "ADA 2026 §10.7 (Statin in diabetes)",
  KDIGO_ACR: "KDIGO 2024 CKD (SGLT2i + ACEi/ARB for albuminuric CKD)",
  ESC_HFrEF: "ESC 2023 HF (Four pillars: ACEi/ARNI, BB, MRA, SGLT2i)",
  RSSDI_INSULIN: "RSSDI India 2024 (Insulin initiation HbA1c >9)",
  BEERS_ELDERLY: "AGS Beers Criteria 2023",
  AHA_ASPIRIN: "AHA 2019 (No routine primary prevention aspirin >70y)",
  AASLD_FIB4: "AASLD 2023 (FIB-4 >2.67 → hepatology referral)",
  KDIGO_NSAID: "KDIGO 2024 (Avoid NSAIDs when eGFR<60)",
  NICE_PPI_CLOPI: "NICE / FDA (Avoid omeprazole/esomeprazole with clopidogrel)",
};

// ================================================================
export function runClinicalRules(input) {
  const {
    profile = {}, labs = {}, medicines = [], allergyFlags = [],
    comorbidities = [], diagnosisPrimary = "",
  } = input || {};

  const alerts = [];
  const requiredLabs = [];
  const interactions = [];

  const add = (level, msg, extra = {}) => alerts.push({ level, msg, ...extra });
  const addLab = (lab, reason, rule_id) => requiredLabs.push({ lab, reason, rule_id });
  const addInter = (pair, level, msg, rule_id) => interactions.push({ pair, level, msg, rule_id });

  // --- lab helpers ---
  const egfr = num(labs.egfr);
  const scr = num(labs.creatinine);
  const k = num(labs.potassium);
  const alt = num(labs.alt);
  const ast = num(labs.ast);
  const hba1c = num(labs.hba1c);
  const ldl = num(labs.ldl);
  const acr = num(labs.acr);
  const na = num(labs.sodium);
  const age = num(profile.age);
  const sbp = num(profile.sbp);
  const bmi = num(profile.bmi);
  const pregnancy = allergyFlags.includes("Pregnancy");
  const breastfeeding = allergyFlags.includes("Breastfeeding");
  const renalImp = allergyFlags.includes("Renal Impairment") || (egfr !== null && egfr < 60);
  const hepImp = allergyFlags.includes("Liver Impairment") || (alt !== null && alt > 120) || (ast !== null && ast > 120);
  const heartFailure = allergyFlags.includes("Heart Failure") || comorbidities.some((c) => /heart failure|hfref/i.test(c));
  const anticoag = allergyFlags.includes("Anticoagulant Use") || has(medicines, "warfarin") || has(medicines, "doac");
  const t2dm = comorbidities.some((c) => /type 2 dm|type-2|t2dm|diabetes/i.test(c)) || /diabetes/i.test(diagnosisPrimary);
  const t1dm = comorbidities.some((c) => /type 1 dm|t1dm/i.test(c));

  // ---------- 1. Metformin rules ----------
  const metformin = findMed(medicines, "metformin");
  if (metformin) {
    if (egfr !== null && egfr < 30) add("block", `Metformin contraindicated (eGFR ${egfr})`, { rule_id: "R-MET-EGFR-30", guideline: G.ADA_METFORMIN, medicine: metformin.brand_name });
    else if (egfr !== null && egfr < 45) add("caution", `Metformin at eGFR ${egfr} — reduce to max 1000 mg/d, monitor renal fn`, { rule_id: "R-MET-EGFR-45", guideline: G.ADA_METFORMIN, medicine: metformin.brand_name });
    if (allergyFlags.includes("History of Pancreatitis") === false && hepImp) add("caution", `Metformin + hepatic impairment — lactic acidosis risk`, { rule_id: "R-MET-HEP", medicine: metformin.brand_name });
    if (has(medicines, "contrast")) add("major", `Hold metformin 48 h around iodinated contrast`, { rule_id: "R-MET-CONTRAST", medicine: metformin.brand_name });
    addLab("Creatinine + eGFR", "Baseline before metformin, then annually", "R-MET-BASE");
    addLab("Vitamin B12", "Long-term metformin → B12 deficiency screen (yearly)", "R-MET-B12");
  }

  // ---------- 2. SGLT2 inhibitors ----------
  const sglt2 = findMed(medicines, "sglt2");
  if (sglt2) {
    if (t1dm) add("block", `SGLT2i in T1DM — high DKA risk`, { rule_id: "R-SGLT-T1", medicine: sglt2.brand_name });
    if (egfr !== null && egfr < 20) add("block", `SGLT2i not recommended at eGFR ${egfr}`, { rule_id: "R-SGLT-EGFR-20", guideline: G.ADA_SGLT2, medicine: sglt2.brand_name });
    else if (egfr !== null && egfr < 45) add("info", `SGLT2i at eGFR ${egfr} — glycaemic effect ↓ but renal/CV benefit retained`, { rule_id: "R-SGLT-EGFR-45", guideline: G.KDIGO_ACR, medicine: sglt2.brand_name });
    if (allergyFlags.includes("Recurrent UTI") || allergyFlags.includes("Genital Infection")) add("caution", `SGLT2i + recurrent UTI/genital infection risk`, { rule_id: "R-SGLT-UTI", medicine: sglt2.brand_name });
    if (allergyFlags.includes("Ketoacidosis")) add("major", `SGLT2i + prior DKA — euglycaemic DKA risk`, { rule_id: "R-SGLT-DKA", medicine: sglt2.brand_name });
    addLab("Creatinine + eGFR", "Baseline before SGLT2i", "R-SGLT-BASE");
  }

  // ---------- 3. Sulfonylurea ----------
  const su = findMed(medicines, "sulfonylurea");
  if (su) {
    if (egfr !== null && egfr < 30) add("major", `Sulfonylurea in eGFR ${egfr} — high hypoglycaemia risk`, { rule_id: "R-SU-EGFR", medicine: su.brand_name });
    if (has(medicines, "insulin")) add("caution", `Sulfonylurea + insulin — added hypoglycaemia risk`, { rule_id: "R-SU-INS" });
    if (age !== null && age >= 65) add("caution", `Sulfonylurea in age ${age} — Beers criteria (hypo)`, { rule_id: "R-SU-ELDER", guideline: G.BEERS_ELDERLY, medicine: su.brand_name });
  }

  // ---------- 4. Pioglitazone ----------
  const pio = findMed(medicines, "pioglitazone");
  if (pio) {
    if (heartFailure) add("block", `Pioglitazone contraindicated in heart failure`, { rule_id: "R-PIO-HF", medicine: pio.brand_name });
    if (comorbidities.some((c) => /bladder cancer/i.test(c))) add("block", `Pioglitazone in history of bladder cancer`, { rule_id: "R-PIO-BLADDER", medicine: pio.brand_name });
  }

  // ---------- 5. Statin ----------
  const statin = findMed(medicines, "statin");
  if (statin) {
    addLab("ALT / AST", "Baseline LFT before statin", "R-STAT-BASE");
    addLab("CK (if muscle symptoms)", "Only if myalgia develops on statin", "R-STAT-CK");
    if (alt !== null && alt > 120) add("caution", `Statin + ALT ${alt} (>3× ULN) — recheck after 4-6 wk`, { rule_id: "R-STAT-ALT", medicine: statin.brand_name });
    if (has(medicines, "fibrate")) addInter([statin.brand_name, findMed(medicines, "fibrate").brand_name], "major", "Statin + fibrate — myopathy / rhabdomyolysis risk", "R-STAT-FIB");
    if (has(medicines, "macrolide")) addInter([statin.brand_name, findMed(medicines, "macrolide").brand_name], "major", "Statin + macrolide — CYP3A4 → myopathy", "R-STAT-MACRO");
    if (has(medicines, "colchicine")) addInter([statin.brand_name, findMed(medicines, "colchicine").brand_name], "major", "Statin + colchicine — myopathy risk", "R-STAT-COLC");
    if (pregnancy) add("block", `Statin contraindicated in pregnancy`, { rule_id: "R-STAT-PREG", medicine: statin.brand_name });
  } else if (t2dm && ldl !== null && ldl >= 100) {
    add("info", `LDL ${ldl} mg/dL in diabetes — consider statin (ADA/ESC)`, { rule_id: "R-STAT-INFO", guideline: G.ADA_LDL });
  }

  // ---------- 6. ACEi / ARB ----------
  const acei = findMed(medicines, "acei");
  const arb = findMed(medicines, "arb");
  const raasi = acei || arb;
  if (raasi) {
    if (k !== null && k > 5.5) add("block", `ACEi/ARB + K⁺ ${k} mEq/L — hyperkalaemia`, { rule_id: "R-RAAS-K", medicine: raasi.brand_name });
    else if (k !== null && k > 5.0) add("caution", `ACEi/ARB + K⁺ ${k} — monitor`, { rule_id: "R-RAAS-K5", medicine: raasi.brand_name });
    if (pregnancy) add("block", `ACEi/ARB contraindicated in pregnancy`, { rule_id: "R-RAAS-PREG", medicine: raasi.brand_name });
    if (has(medicines, "spironolactone") && egfr !== null && egfr < 30) add("major", `ACEi/ARB + spironolactone at eGFR ${egfr} — hyperK risk`, { rule_id: "R-RAAS-SPIRO" });
    if (has(medicines, "nsaid")) addInter([raasi.brand_name, findMed(medicines, "nsaid").brand_name], "major", "ACEi/ARB + NSAID — AKI risk", "R-RAAS-NSAID");
    if (acei && arb) add("major", `Dual ACEi + ARB combination — avoid`, { rule_id: "R-RAAS-DUAL" });
    addLab("Creatinine + K⁺", "At 1-2 weeks after RAASi start / dose change", "R-RAAS-BASE");
  }

  // ---------- 7. NSAIDs ----------
  const nsaid = findMed(medicines, "nsaid");
  if (nsaid) {
    if (egfr !== null && egfr < 60) add("caution", `NSAID + eGFR ${egfr} — avoid if possible`, { rule_id: "R-NSAID-EGFR", guideline: G.KDIGO_NSAID, medicine: nsaid.brand_name });
    if (egfr !== null && egfr < 30) add("block", `NSAID contraindicated (eGFR ${egfr})`, { rule_id: "R-NSAID-BLOCK", medicine: nsaid.brand_name });
    if (anticoag) addInter([nsaid.brand_name, "Anticoagulant"], "major", "NSAID + anticoagulant — GI bleed risk", "R-NSAID-AC");
    if (has(medicines, "aspirin")) addInter([nsaid.brand_name, "Aspirin"], "caution", "NSAID + aspirin — GI bleed risk", "R-NSAID-ASA");
  }

  // ---------- 8. Nitrate / PDE5 ----------
  if (has(medicines, "nitrate") && has(medicines, "pde5"))
    addInter([findMed(medicines, "nitrate").brand_name, findMed(medicines, "pde5").brand_name], "block", "Nitrate + PDE5i — severe hypotension", "R-NIT-PDE5");

  // ---------- 9. Beta-blocker + non-DHP CCB ----------
  if (has(medicines, "bb") && has(medicines, "ccb_ndhp"))
    addInter([findMed(medicines, "bb").brand_name, findMed(medicines, "ccb_ndhp").brand_name], "major", "β-blocker + verapamil/diltiazem — heart block risk", "R-BB-CCB");

  // ---------- 10. QT prolongers ----------
  const qtDrugs = medicines.filter((m) => drugClass(m).includes("qtProlonging"));
  if (qtDrugs.length >= 2) add("major", `Multiple QT-prolonging drugs: ${qtDrugs.map((m) => m.brand_name).join(", ")}`, { rule_id: "R-QT-MULTI" });
  if (qtDrugs.length > 0 && allergyFlags.includes("QT Prolongation")) add("major", `QT-prolonger on background QT prolongation`, { rule_id: "R-QT-HX" });

  // ---------- 11. Digoxin + Amiodarone ----------
  if (has(medicines, "digoxin") && has(medicines, "amiodarone"))
    addInter(["Digoxin", "Amiodarone"], "major", "Amiodarone raises digoxin level — reduce digoxin 50%", "R-DIG-AMIO");

  // ---------- 12. Levothyroxine spacing ----------
  if (has(medicines, "levothyroxine") && has(medicines, "calcium_iron"))
    addInter(["Levothyroxine", "Calcium/Iron"], "caution", "Levothyroxine absorption ↓ by Ca/Fe — separate by 4 hours", "R-LT4-CA");

  // ---------- 13. PPI + Clopidogrel ----------
  const clopi = findMed(medicines, "clopidogrel");
  const ppi = findMed(medicines, "ppi");
  if (clopi && ppi && /(omeprazole|esomeprazole)/i.test(medText(ppi)))
    addInter([clopi.brand_name, ppi.brand_name], "caution", "Omeprazole/esomeprazole ↓ clopidogrel activation — use pantoprazole", "R-PPI-CLOPI", G.NICE_PPI_CLOPI);

  // ---------- 14. Warfarin interactions ----------
  const warf = findMed(medicines, "warfarin");
  if (warf) {
    if (has(medicines, "nsaid")) addInter([warf.brand_name, findMed(medicines, "nsaid").brand_name], "major", "Warfarin + NSAID — major bleed risk", "R-WARF-NSAID");
    if (has(medicines, "fq")) addInter([warf.brand_name, findMed(medicines, "fq").brand_name], "caution", "Fluoroquinolone + warfarin — INR ↑; recheck in 3-5 days", "R-WARF-FQ");
    if (has(medicines, "macrolide")) addInter([warf.brand_name, findMed(medicines, "macrolide").brand_name], "caution", "Macrolide + warfarin — INR ↑", "R-WARF-MACRO");
    addLab("INR", "Baseline + weekly until stable", "R-WARF-BASE");
  }

  // ---------- 15. Amiodarone workup ----------
  if (has(medicines, "amiodarone")) {
    addLab("TFT + LFT + CXR", "Baseline before amiodarone; TFT/LFT 6-monthly", "R-AMIO-BASE");
  }

  // ---------- 16. Elderly + benzo (Beers) ----------
  if (age !== null && age >= 65 && has(medicines, "benzo"))
    add("caution", `Benzodiazepine in age ${age} — falls / cognitive decline`, { rule_id: "R-BENZO-ELDER", guideline: G.BEERS_ELDERLY });

  // ---------- 17. HbA1c > 9 without insulin/GLP-1 ----------
  if (t2dm && hba1c !== null && hba1c >= 9 && !has(medicines, "insulin") && !has(medicines, "glp1"))
    add("info", `HbA1c ${hba1c}% — consider insulin or GLP-1 RA`, { rule_id: "R-HBA1C-9", guideline: G.RSSDI_INSULIN });

  // ---------- 18. Albuminuric CKD without SGLT2i ----------
  if (t2dm && acr !== null && acr >= 30 && egfr !== null && egfr >= 20 && !has(medicines, "sglt2"))
    add("info", `ACR ${acr} mg/g + T2DM — SGLT2i recommended`, { rule_id: "R-ACR-SGLT2", guideline: G.KDIGO_ACR });

  // ---------- 19. HFrEF without SGLT2i ----------
  if (heartFailure && !has(medicines, "sglt2"))
    add("info", `Heart failure — SGLT2i indicated (ESC 4 pillars)`, { rule_id: "R-HF-SGLT2", guideline: G.ESC_HFrEF });

  // ---------- 20. FIB-4 high without hepatology plan ----------
  const fib4v = num(labs.fib4);
  if (fib4v !== null && fib4v > 2.67)
    add("info", `FIB-4 ${fib4v} — high fibrosis risk, consider hepatology referral`, { rule_id: "R-FIB4-HIGH", guideline: G.AASLD_FIB4 });

  // ---------- 21. Aspirin primary prevention in elderly ----------
  if (age !== null && age >= 70 && has(medicines, "aspirin") && !comorbidities.some((c) => /cad|mi|stroke|tia|pad/i.test(c)))
    add("caution", `Aspirin primary prevention age ${age} — bleed vs benefit`, { rule_id: "R-ASA-ELDER", guideline: G.AHA_ASPIRIN });

  // ---------- 22. Duplicate generics ----------
  const seen = new Map();
  medicines.forEach((m, idx) => {
    const g = clean(m.generic_name);
    if (!g) return;
    if (seen.has(g)) add("major", `Duplicate generic: ${m.generic_name} (rows ${seen.get(g) + 1} & ${idx + 1})`, { rule_id: "R-DUP" });
    else seen.set(g, idx);
  });

  // ---------- 23. Pregnancy blanket flags ----------
  if (pregnancy) {
    medicines.forEach((m) => {
      if (/statin|ramipril|losartan|telmisartan|warfarin|methotrexate|isotretinoin/i.test(medText(m)))
        add("block", `${m.brand_name} contraindicated in pregnancy`, { rule_id: "R-PREG", medicine: m.brand_name });
    });
  }
  return { alerts, requiredLabs, interactions };
}

// Convenience: level ordering for sorting
export const LEVEL_ORDER = { block: 0, major: 1, caution: 2, info: 3 };
